import {
  Badge,
  Box,
  ComboboxItem,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  Grid,
} from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import { IconBell, IconChartBar, IconHash, IconUser } from "@tabler/icons-react"

import { Filters } from "@costory/types/filters"

import { formatNumber } from "@costory/shared/utils/format"

import { StackedBarChart } from "@costory/front/components/charts/StackedBarChart"
import { AlertFormData } from "@costory/front/pages/Alerts/AlertForm"

type Props = {
  form: UseFormReturnType<AlertFormData>
  filters: Filters | undefined
  selectedOwner: ComboboxItem | undefined
  existingSlackChannels: {
    label: string
    value: string
  }[]
  selectedVirtualDimensionLabel?: string
}

const AlertPreview = ({
  form,
  filters,
  selectedOwner,
  selectedVirtualDimensionLabel,
  existingSlackChannels,
}: Props) => {
  const referenceLines = [
    {
      y: form.values.redLimit,
      stroke: "red",
      strokeWidth: 2,
    },
    {
      y: form.values.orangeLimit,
      stroke: "orange",
      strokeWidth: 2,
    },
    {
      y: form.values.yellowLimit,
      stroke: "yellow",
      strokeWidth: 2,
    },
  ].filter((line) => !!line)

  return (
    <Grid.Col span={{ base: 12, md: 5 }}>
      <Paper shadow="xs" p="lg" radius="md" withBorder>
        <Group mb="md">
          <IconBell size={20} />
          <Title order={3}>Alert Preview</Title>
        </Group>
        <Text c="dimmed" mb="xl">
          How your alert will behave
        </Text>

        <Group align="flex-start" gap={4} mb="xl" display="flex" wrap="nowrap">
          <Box flex={1} miw={0}>
            <Text fw={500} mb="xs">
              Monitoring
            </Text>
            <Text style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
              {selectedVirtualDimensionLabel}:{" "}
              {form.values.virtualDimensionValue}
            </Text>
          </Box>
          {selectedOwner ? (
            <Box flex={1} miw={0}>
              <Text fw={500} mb="xs">
                Notifications
              </Text>
              {selectedOwner && (
                <Group gap="xs" wrap="nowrap">
                  <IconUser size={16} />
                  <Text
                    style={{ whiteSpace: "normal", wordBreak: "break-word" }}
                  >
                    {selectedOwner.label}
                  </Text>
                </Group>
              )}
              {!!form.values.slackChannelIds.length && (
                <Group gap="xs" mt="xs" wrap="wrap">
                  <IconHash size={16} />
                  {form.values.slackChannelIds.map((id: string) => {
                    const channel = existingSlackChannels.find(
                      (ch) => ch.value === id,
                    )
                    return (
                      channel && (
                        <Badge key={id} color="textMuted" variant="light">
                          {channel.label}
                        </Badge>
                      )
                    )
                  })}
                </Group>
              )}
            </Box>
          ) : (
            <></>
          )}
        </Group>

        <Box mb="xl">
          <Group justify="space-between" mb="xs">
            <Text fw={500}>Threshold levels</Text>
            {form.values.useForecastedValues && (
              <Text size="sm" c="blue" fw={500}>
                Using forecasted values
              </Text>
            )}
          </Group>
          {form.values.useForecastedValues && (
            <Text size="sm" c="dimmed" mb="md">
              Alert will trigger based on forecasted end-of-period costs (e.g.,
              if on day 6 of the month, forecast shows exceeding limit by month
              end)
            </Text>
          )}
          <Group gap="lg" mb="md">
            {[
              { color: "red", value: form.values.redLimit },
              { color: "orange", value: form.values.orangeLimit },
              { color: "yellow", value: form.values.yellowLimit },
            ].map((limit) => (
              <Stack gap={5} w="30%" align="center" key={limit.color}>
                <Box
                  mb={5}
                  h={6}
                  bg={limit.color}
                  style={{ borderRadius: 5 }}
                  w="100%"
                />
                <Text fw={500}>
                  {formatNumber({ value: limit.value ?? 0 })} USD
                </Text>
              </Stack>
            ))}
          </Group>
        </Box>

        {filters ? (
          <Box mb="xl">
            <Text fw={500} mb="xs">
              Historical data
            </Text>
            <Box h={250}>
              <StackedBarChart
                filters={filters}
                referenceLines={referenceLines}
                isAlertPreview={true}
              />
            </Box>
          </Box>
        ) : (
          <Stack align="center">
            <IconChartBar size={64} strokeWidth={1} />
            <Text size="xl" ta="center">
              Configure the alert to see the preview
            </Text>
          </Stack>
        )}
      </Paper>
    </Grid.Col>
  )
}

export default AlertPreview
